import {
  DialogVehicleWidgetName,
  FacetTilesVehicleWidgetName,
  HeaderVehicleWidgetName,
  HomeVehicleWidgetName,
  VehicleWidgetDialogName,
} from 'Components/names';
import { storeId } from 'Models/serverConfiguration';
import { mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import type { FitmentSearchCallbackConfig } from 'Models/uiConfig/fitmentSearchConfig.types';
import defaultFitmentSearchConfig from 'Stores/_default-store/fitmentSearchConfig';
import { safeCallback } from 'Utils/safeCallback';
import { trimEndSlash } from 'Utils/string';
import t from 'Utils/translate';
import { addPlatformRoute } from 'Utils/url';

const {
  fitmentSearch: {
    autoSyncKey: configuredAutoSyncKey,
    baseFields = [],
    categorySelectionPageRedirect,
    categorySelectionPages,
    disableVehicleStorage,
    doNotLoadSelectedVehicle = false,
    extraFields = [],
    isExtraFieldsAlwaysVisible = false,
    forceSpecifyDialog,
    forceVehicleSelection = false,
    forceVehicleSelectionInterval = 60, // one hour
    getFitmentSearchTitle = () => t('ymm_bar_vehicle_subtitle'),
    isAutoRedirectDisabled,
    isAutoVehicleSelectionDisabled,
    hideVehicleFromUrl = false,
    getVerifyFitmentOrderNotes: customGetVerifyFitmentOrderNotes,
    onVehicleDiscarded: customOnVehicleDiscarded,
    onVehicleSelected: customOnVehicleSelected,
    onVerifyFitmentInitResponseReceived: customOnVerifyFitmentInitResponseReceived,
    onVerifyFitmentResponseReceived: customOnVerifyFitmentResponseReceived,
    hideVerifyOnCartItems = false,
    vehicleDataExpires,
  } = {},
  Widgets,
} = mainConfig;

const fitmentFields = [...baseFields, ...extraFields];

// Legacy ContextDialogs with VehicleTabs or VehicleTiles still depend on the
// standard name until those components initialize their own isolated requests.
const vehicleDialogNames = new Set([
  VehicleWidgetDialogName,
  ...Widgets.filter((widget) => widget.type === VehicleWidgetDialogName).map((widget) => widget.name),
]);

// The connected config.js supplies the backend AutoSync key before search.js loads.
// Stores using that script can omit an explicit autoSyncKey equal to their store ID.
const runtimeAutoSyncKey = window.Convermax.config?.autoSyncKey;
const connectedAutoSyncKey =
  configuredAutoSyncKey ?? (typeof runtimeAutoSyncKey === 'string' ? runtimeAutoSyncKey : undefined);
const autoSyncKey = connectedAutoSyncKey ?? storeId;

const isolatedHeader = 'isolatedHeader';
const isolatedDialog = 'isolatedDialog';

const categorySelectionPageList = (
  Array.isArray(categorySelectionPages)
    ? categorySelectionPages
    : categorySelectionPages
      ? [categorySelectionPages]
      : []
).map((page) => ({ ...page, pathname: addPlatformRoute(page.pathname) }));
const { pathname: categorySelectionPageUrl, field: categorySelectionPageField } =
  categorySelectionPageList[0] ?? {};

const currentCategorySelectionPage =
  categorySelectionPageList.find(
    (page) => trimEndSlash(window.location.pathname) === trimEndSlash(page.pathname),
  ) ?? null;
const currentCategorySelectionPageField = currentCategorySelectionPage?.field ?? null;
const isOnCategorySelectionPage = !!currentCategorySelectionPageField;
const hasSearchResults = Widgets.some((widget) => widget.visible && widget.hasSearchResults);

const isFacetTilesVehicleWidgetEnabled = Widgets.some(
  (widget) =>
    widget.visible && widget.type === 'FacetTiles' && !hasSearchResults && !widget.isVehicleWidgetDisabled,
);

const isFitmentSearchWidgetsVisible =
  isFacetTilesVehicleWidgetEnabled ||
  Widgets.some((widget) => {
    const [nameRoot] = widget.name.split('_');
    return (
      nameRoot !== DialogVehicleWidgetName &&
      widget.visible &&
      (widget.type === 'VehicleTabs' ||
        widget.type === 'VehicleWidget' ||
        widget.type === 'VehicleTiles' ||
        widget.type === 'VerifyFitment')
    );
  });

function getWidgetIsolatedKey(name: string): typeof isolatedHeader | typeof isolatedDialog | null {
  const [nameRoot] = name.split('_');
  switch (nameRoot) {
    case DialogVehicleWidgetName:
      return isolatedDialog;
    case HomeVehicleWidgetName:
    case HeaderVehicleWidgetName:
    case FacetTilesVehicleWidgetName:
      return !hasSearchResults ? isolatedHeader : null;
    default:
      return null;
  }
}

const needInitFitmentSearchRequest =
  isFacetTilesVehicleWidgetEnabled ||
  Widgets.map((widget) => widget.visible && getWidgetIsolatedKey(widget.name)).includes(isolatedHeader);
const needInitFitmentTableRequest = Widgets.some(
  (widget) => widget.visible && widget.type === 'FitmentTable',
);
const needInitVerifyFitmentRequest = Widgets.some(
  (widget) => widget.visible && widget.type === 'VerifyFitment',
);
const verifyFitmentDropdownsWithProductId = Widgets.some(
  (widget) => widget.type === 'VerifyFitment' && widget.dropdownsWithProductId === true,
);

const defaults: FitmentSearchCallbackConfig = defaultFitmentSearchConfig || {
  getVerifyFitmentOrderNotes: null,
  onVehicleSelected: null,
  onVehicleDiscarded: null,
  onVerifyFitmentInitResponseReceived: null,
  onVerifyFitmentResponseReceived: null,
};

const onVehicleSelected = safeCallback((vehicle) => {
  defaults.onVehicleSelected?.(vehicle);
  customOnVehicleSelected?.(vehicle);
  window.Convermax.onVehicleSelected?.(vehicle);
}, 'on_vehicle_selected');

const onVehicleDiscarded = safeCallback(() => {
  defaults.onVehicleDiscarded?.();
  customOnVehicleDiscarded?.();
}, 'on_vehicle_discarded');

const onVerifyFitmentInitResponseReceived = safeCallback((response) => {
  defaults.onVerifyFitmentInitResponseReceived?.(response);
  customOnVerifyFitmentInitResponseReceived?.(response);
}, 'on_verify_fitment_init_response');

const onVerifyFitmentResponseReceived = safeCallback((response) => {
  defaults.onVerifyFitmentResponseReceived?.(response);
  customOnVerifyFitmentResponseReceived?.(response);
}, 'on_verify_fitment_response');

const getVerifyFitmentOrderNotes = (fits: string): string | undefined => {
  const vehicleString = window.Convermax.getVehicleString();
  const vehicle = window.Convermax.getVehicle();
  const fieldsToSpecify = extraFields.filter((field) => !(field in vehicle)).join(', ');

  return (
    customGetVerifyFitmentOrderNotes?.(fits, vehicleString, fieldsToSpecify) ??
    defaults.getVerifyFitmentOrderNotes?.(fits, vehicleString, fieldsToSpecify)
  );
};

const fitmentSearchConfig = {
  autoSyncConnected: !!connectedAutoSyncKey,
  autoSyncKey,
  baseFields,
  categorySelectionPageField,
  categorySelectionPageUrl,
  currentCategorySelectionPageField,
  disableVehicleStorage,
  doNotLoadSelectedVehicle,
  extraFields,
  fields: fitmentFields,
  fitmentSearchTitle: getFitmentSearchTitle() || t('ymm_bar_vehicle_subtitle'),
  forceSpecifyDialog,
  forceVehicleSelection,
  forceVehicleSelectionInterval,
  hideVehicleFromUrl,
  isCategorySelectionPageRedirectEnabled: categorySelectionPageRedirect,
  isExtraFieldsAlwaysVisible,
  isOnCategorySelectionPage,
  isAutoRedirectDisabled,
  isAutoVehicleSelectionDisabled,
  isFitmentSearchWidgetsVisible,
  isAnyVehicleWidgetIsolated: Widgets.some(
    (widget) => widget.type === 'VehicleWidget' && widget.visible && widget.isVehicleSelectionIsolated,
  ),
  isVehicleWidgetInPartialMode: Widgets.some(
    (widget) => (widget.type === 'VehicleWidget' && widget.fields) || widget.type === 'VehicleTabs',
  ),
  needInitFitmentSearchRequest,
  needInitFitmentTableRequest,
  needInitVerifyFitmentRequest,
  verifyFitmentDropdownsWithProductId,
  getVerifyFitmentOrderNotes,
  onVehicleDiscarded,
  onVehicleSelected,
  onVerifyFitmentInitResponseReceived,
  onVerifyFitmentResponseReceived,
  hideVerifyOnCartItems,
  vehicleDataExpires,
  vehicleDialogNames,
};

window.Convermax.templates!.config.fitmentSearchConfig = fitmentSearchConfig;
export default fitmentSearchConfig;
